// useProjectWeeklyTasks.js - Data hook for Project Weekly Tasks + Subtasks
// Location: src/hooks/useProjectWeeklyTasks.js
//
// Follows the same patterns as useHFBData.js:
//   • snake_case in DB → camelCase in UI
//   • requireSession() before mutations
//   • { success, data?, error? } return shape
//   • debugLog / debugError gated by NODE_ENV
//
// ── Carry-forward logic ─────────────────────────────────────────────────
// Tasks use `origin_week_id` to track the week they were created in.
// When viewing a selected weekId, the hook fetches:
//   1. Tasks whose origin_week_id == weekId  (native to this week)
//   2. Tasks whose origin_week_id <  weekId AND progress < 100  (carried)
// Each returned task includes `isCarriedForward: boolean`.
// No rows are duplicated — carry-forward is purely a computed view.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { requireSession } from '../lib/supabaseAuthHelpers';

// ── Realtime sync (on by default) ────────────────────────────────────────
const ENABLE_REALTIME_PWT_DEFAULT = true;

// ── Debug logging (off in production) ────────────────────────────────────
const DEBUG_PWT = process.env.NODE_ENV !== 'production';

function debugLog(...args) {
  if (DEBUG_PWT) console.log('[PWT]', ...args);
}
function debugError(...args) {
  if (DEBUG_PWT) console.error('[PWT]', ...args);
}

// ── Normalize DB row → camelCase ─────────────────────────────────────────
// `selectedWeekId` is passed in so we can compute isCarriedForward at
// normalize time rather than requiring a second pass.
function normalizeTask(row, selectedWeekId) {
  var originWeekId = row.origin_week_id || row.week_id; // fallback for un-migrated rows
  return {
    id: row.id,
    projectId: row.project_id,
    weekId: row.week_id,
    originWeekId: originWeekId,
    task: row.task,
    subTask: row.sub_task || '',      // legacy column, kept for compat
    categories: row.categories || [],
    priority: row.priority || null,
    updates5pm: row.updates_5pm || '',
    startDate: row.start_date || null,
    completionDate: row.completion_date || null,
    assignedUserId: row.assigned_user_id || null,    // legacy single-assign, kept for compat
    assignedUserIds: row.assigned_user_ids || [],
    progress: row.progress ?? 0,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    isCarriedForward: selectedWeekId ? originWeekId < selectedWeekId : false
  };
}

function normalizeSubtask(row) {
  return {
    id: row.id,
    parentTaskId: row.parent_task_id,
    subTask: row.sub_task || '',
    startDate: row.start_date || null,
    completionDate: row.completion_date || null,
    assignedUserId: row.assigned_user_id || null,
    progress: row.progress ?? 0,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

// ── Helper: validate project_id is numeric (bigint-safe) ─────────────────
function toProjectId(value) {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    var parsed = Number(value);
    if (!isNaN(parsed)) return parsed;
  }
  return null;
}

// ── Helper: coerce user id to number for int8 column ─────────────────────
function toUserId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  var parsed = Number(value);
  return isNaN(parsed) ? null : parsed;
}

// ── Helper: find which parentTaskId owns a given subtaskId ───────────────
function findParentForSubtask(subtasksByParent, subtaskId) {
  var parentIds = Object.keys(subtasksByParent);
  for (var i = 0; i < parentIds.length; i++) {
    var list = subtasksByParent[parentIds[i]];
    for (var j = 0; j < list.length; j++) {
      if (list[j].id === subtaskId) return parentIds[i];
    }
  }
  return null;
}

// ── Debounce hook (matches useHFBData pattern) ──────────────────────────
function useDebouncedCallback(callback, delay) {
  var timeoutRef = useRef(null);
  var callbackRef = useRef(callback);

  useEffect(function () { callbackRef.current = callback; }, [callback]);

  var debouncedFn = useCallback(function () {
    var args = arguments;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(function () {
      callbackRef.current.apply(null, args);
    }, delay);
  }, [delay]);

  useEffect(function () {
    return function () {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return debouncedFn;
}

// ══════════════════════════════════════════════════════════════════════════
// HOOK: useProjectWeeklyTasks(projectId, weekId)
//
// Returns {
//   tasks, subtasksByParent, isLoading, error, refetch,
//   addTask, updateTask, deleteTask,
//   addSubtask, updateSubtask, deleteSubtask
// }
// ══════════════════════════════════════════════════════════════════════════
export function useProjectWeeklyTasks(projectId, weekId, enableRealtime) {
  if (enableRealtime === undefined) enableRealtime = ENABLE_REALTIME_PWT_DEFAULT;
  const [tasks, setTasks] = useState([]);
  const [subtasksByParent, setSubtasksByParent] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);
  const lastKeyRef = useRef(null);

  // Keep a ref to subtasksByParent so lookup helpers have fresh data
  const subtasksRef = useRef(subtasksByParent);
  useEffect(function () { subtasksRef.current = subtasksByParent; }, [subtasksByParent]);

  // ── Fetch tasks + subtasks for project + week (with carry-forward) ────
  const fetchTasks = useCallback(async function (force) {
    var numericProjectId = toProjectId(projectId);
    if (!numericProjectId || !weekId) {
      setTasks([]);
      setSubtasksByParent({});
      return [];
    }

    var fetchKey = numericProjectId + ':' + weekId;
    if (isFetchingRef.current && !force) return tasks;
    if (!force && lastKeyRef.current === fetchKey && tasks.length > 0) return tasks;

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      debugLog('Fetching tasks (carry-forward) for', { projectId: numericProjectId, weekId });

      // ── Carry-forward query ───────────────────────────────────────
      // Fetch tasks where:
      //   origin_week_id <= weekId
      //   AND (origin_week_id = weekId OR progress < 100)
      //
      // This returns:
      //   • All tasks native to this week (regardless of progress)
      //   • All incomplete tasks from earlier weeks (carried forward)
      //
      // Supabase PostgREST .or() filter combines with the preceding
      // .eq() and .lte() via AND, producing:
      //   project_id = X AND origin_week_id <= weekId
      //   AND (origin_week_id = weekId OR progress < 100)
      var tasksResponse = await supabase
        .from('project_weekly_tasks')
        .select('*')
        .eq('project_id', numericProjectId)
        .lte('origin_week_id', weekId)
        .or('origin_week_id.eq.' + weekId + ',progress.lt.100')
        .order('origin_week_id', { ascending: true })
        .order('created_at', { ascending: true });

      if (tasksResponse.error) {
        debugError('Fetch tasks error:', tasksResponse.error);
        setError(tasksResponse.error.message || 'Failed to fetch tasks');
        setTasks([]);
        setSubtasksByParent({});
        return [];
      }

      var normalizedTasks = (tasksResponse.data || []).map(function (row) {
        return normalizeTask(row, weekId);
      });

      // Deduplicate by id (safety net — shouldn't happen with correct query)
      var seenIds = {};
      normalizedTasks = normalizedTasks.filter(function (t) {
        if (seenIds[t.id]) return false;
        seenIds[t.id] = true;
        return true;
      });

      var parentIds = normalizedTasks.map(function (t) { return t.id; });

      var groupedSubtasks = {};
      if (parentIds.length > 0) {
        var subtasksResponse = await supabase
          .from('project_weekly_subtasks')
          .select('*')
          .in('parent_task_id', parentIds)
          .order('created_at', { ascending: true });

        if (subtasksResponse.error) {
          debugError('Fetch subtasks error:', subtasksResponse.error);
        } else {
          (subtasksResponse.data || []).forEach(function (row) {
            var st = normalizeSubtask(row);
            if (!groupedSubtasks[st.parentTaskId]) {
              groupedSubtasks[st.parentTaskId] = [];
            }
            groupedSubtasks[st.parentTaskId].push(st);
          });
        }
      }

      var carriedCount = normalizedTasks.filter(function (t) { return t.isCarriedForward; }).length;
      debugLog('Fetched', normalizedTasks.length, 'tasks (' + carriedCount + ' carried forward),',
        Object.keys(groupedSubtasks).length, 'parents with subtasks');

      setTasks(normalizedTasks);
      setSubtasksByParent(groupedSubtasks);
      lastKeyRef.current = fetchKey;
      return normalizedTasks;
    } catch (err) {
      debugError('Fetch tasks exception:', err);
      setError(err.message || 'Unexpected error fetching tasks');
      setTasks([]);
      setSubtasksByParent({});
      return [];
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [projectId, weekId, tasks]);

  useEffect(function () {
    var numericProjectId = toProjectId(projectId);
    var newKey = numericProjectId && weekId ? numericProjectId + ':' + weekId : null;
    if (newKey && newKey !== lastKeyRef.current) {
      fetchTasks(true);
    } else if (!newKey) {
      setTasks([]);
      setSubtasksByParent({});
      lastKeyRef.current = null;
    }
  }, [projectId, weekId]); // eslint-disable-line react-hooks/exhaustive-deps

  var refetch = useCallback(function () {
    isFetchingRef.current = false;
    lastKeyRef.current = null;
    return fetchTasks(true);
  }, [fetchTasks]);

  // Stable ref so the visibility listener always calls the latest refetch
  // without being included in effect deps (avoids re-adding listener on every task update).
  var refetchStableRef = useRef(null);
  refetchStableRef.current = refetch;

  // ══════════════════════════════════════════════════════════════════════
  // PARENT TASK MUTATIONS
  // ══════════════════════════════════════════════════════════════════════

  var addTask = async function (overrides) {
    var numericProjectId = toProjectId(projectId);
    if (!numericProjectId || !weekId) {
      return { success: false, error: 'Missing projectId or weekId' };
    }

    var user;
    try {
      var sessionResult = await requireSession();
      user = sessionResult.user;
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    var payload = {
      project_id: numericProjectId,
      week_id: weekId,
      origin_week_id: weekId,         // ← carry-forward: stamp origin week
      task: (overrides && overrides.task) || 'New task',
      sub_task: null,
      categories: (overrides && overrides.categories) || [],
      priority: (overrides && overrides.priority) || null,
      updates_5pm: (overrides && overrides.updates5pm) || '',
      start_date: (overrides && overrides.startDate) || null,
      completion_date: (overrides && overrides.completionDate) || null,
      assigned_user_id: toUserId(overrides && overrides.assignedUserId),
      assigned_user_ids: (overrides && overrides.assignedUserIds) || [],
      progress: (overrides && overrides.progress) || 0,
      created_by: user.id
    };

    debugLog('addTask payload:', payload);

    try {
      var response = await supabase
        .from('project_weekly_tasks')
        .insert(payload)
        .select()
        .single();

      if (response.error) {
        debugError('addTask error:', response.error);
        return { success: false, error: response.error.message };
      }

      var newTask = normalizeTask(response.data, weekId);
      debugLog('addTask success:', newTask.id);

      setTasks(function (prev) { return [newTask, ...prev]; });
      return { success: true, data: newTask };
    } catch (err) {
      debugError('addTask exception:', err);
      return { success: false, error: err.message };
    }
  };

  var updateTask = async function (taskId, fields) {
    if (!taskId) return { success: false, error: 'Missing task ID' };

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    var dbFields = {};
    if (fields.task !== undefined) dbFields.task = fields.task;
    if (fields.subTask !== undefined) dbFields.sub_task = fields.subTask;
    if (fields.categories !== undefined) dbFields.categories = fields.categories;
    if (fields.priority !== undefined) dbFields.priority = fields.priority || null;
    if (fields.updates5pm !== undefined) dbFields.updates_5pm = fields.updates5pm;
    if (fields.startDate !== undefined) dbFields.start_date = fields.startDate || null;
    if (fields.completionDate !== undefined) dbFields.completion_date = fields.completionDate || null;
    if (fields.assignedUserId !== undefined) dbFields.assigned_user_id = toUserId(fields.assignedUserId);
    if (fields.assignedUserIds !== undefined) dbFields.assigned_user_ids = fields.assignedUserIds;
    if (fields.progress !== undefined) dbFields.progress = fields.progress;

    debugLog('updateTask', taskId, dbFields);

    try {
      var response = await supabase
        .from('project_weekly_tasks')
        .update(dbFields)
        .eq('id', taskId)
        .select()
        .single();

      if (response.error) {
        debugError('updateTask error:', response.error);
        return { success: false, error: response.error.message };
      }

      var updated = normalizeTask(response.data, weekId);
      debugLog('updateTask success:', updated.id);

      setTasks(function (prev) {
        return prev.map(function (t) { return t.id === taskId ? updated : t; });
      });
      return { success: true, data: updated };
    } catch (err) {
      debugError('updateTask exception:', err);
      return { success: false, error: err.message };
    }
  };

  var deleteTask = async function (taskId) {
    if (!taskId) return { success: false, error: 'Missing task ID' };

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    debugLog('deleteTask', taskId);

    try {
      var response = await supabase
        .from('project_weekly_tasks')
        .delete()
        .eq('id', taskId)
        .select('id');  // request deleted rows back so we can confirm deletion happened

      if (response.error) {
        debugError('deleteTask error:', response.error);
        if (response.error.code === '42501') {
          return { success: false, error: 'Permission denied. Only supervisors and admins can delete tasks.' };
        }
        return { success: false, error: response.error.message };
      }

      // If RLS silently blocked the delete (no error but no rows removed), bail out.
      // Without this check the task vanishes from the UI then reappears on the next refetch.
      if (!response.data || response.data.length === 0) {
        debugError('deleteTask: no rows deleted — permission denied or task already removed');
        return { success: false, error: 'Task could not be deleted. You may not have permission or it was already removed.' };
      }

      debugLog('deleteTask success:', taskId);

      setTasks(function (prev) {
        return prev.filter(function (t) { return t.id !== taskId; });
      });
      setSubtasksByParent(function (prev) {
        var next = {};
        Object.keys(prev).forEach(function (k) {
          if (k !== taskId) next[k] = prev[k];
        });
        return next;
      });
      return { success: true };
    } catch (err) {
      debugError('deleteTask exception:', err);
      return { success: false, error: err.message };
    }
  };

  // ══════════════════════════════════════════════════════════════════════
  // SUBTASK MUTATIONS
  //
  // Simplified signatures: updateSubtask(id, fields) / deleteSubtask(id)
  // parentTaskId is resolved internally from subtasksByParent map.
  // ══════════════════════════════════════════════════════════════════════

  var addSubtask = async function (parentTaskId, overrides) {
    if (!parentTaskId) {
      return { success: false, error: 'Missing parent task ID' };
    }

    var user;
    try {
      var sessionResult = await requireSession();
      user = sessionResult.user;
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    var payload = {
      parent_task_id: parentTaskId,
      sub_task: (overrides && overrides.subTask) || 'New subtask',
      start_date: (overrides && overrides.startDate) || null,
      completion_date: (overrides && overrides.completionDate) || null,
      assigned_user_id: toUserId(overrides && overrides.assignedUserId),
      progress: (overrides && overrides.progress) || 0,
      created_by: user.id
    };

    debugLog('addSubtask payload:', payload);

    try {
      var response = await supabase
        .from('project_weekly_subtasks')
        .insert(payload)
        .select()
        .single();

      if (response.error) {
        debugError('addSubtask error:', response.error);
        return { success: false, error: response.error.message };
      }

      var newSubtask = normalizeSubtask(response.data);
      debugLog('addSubtask success:', newSubtask.id);

      setSubtasksByParent(function (prev) {
        var parentList = prev[parentTaskId] ? [...prev[parentTaskId]] : [];
        parentList.push(newSubtask);
        return { ...prev, [parentTaskId]: parentList };
      });
      return { success: true, data: newSubtask };
    } catch (err) {
      debugError('addSubtask exception:', err);
      return { success: false, error: err.message };
    }
  };

  // updateSubtask(subtaskId, fields) — 2 args, resolves parent internally
  var updateSubtask = async function (subtaskId, fields) {
    if (!subtaskId) return { success: false, error: 'Missing subtask ID' };
    if (!fields || typeof fields !== 'object') {
      return { success: false, error: 'Missing fields object' };
    }

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    var dbFields = {};
    if (fields.subTask !== undefined) dbFields.sub_task = fields.subTask;
    if (fields.startDate !== undefined) dbFields.start_date = fields.startDate || null;
    if (fields.completionDate !== undefined) dbFields.completion_date = fields.completionDate || null;
    if (fields.assignedUserId !== undefined) dbFields.assigned_user_id = toUserId(fields.assignedUserId);
    if (fields.progress !== undefined) dbFields.progress = fields.progress;

    debugLog('updateSubtask', subtaskId, dbFields);

    // Optimistic local update (instant UI feedback before network round-trip)
    var resolvedParent = findParentForSubtask(subtasksRef.current, subtaskId);
    if (resolvedParent) {
      setSubtasksByParent(function (prev) {
        var list = prev[resolvedParent] || [];
        return {
          ...prev,
          [resolvedParent]: list.map(function (s) {
            if (s.id !== subtaskId) return s;
            var patched = { ...s };
            if (fields.subTask !== undefined) patched.subTask = fields.subTask;
            if (fields.startDate !== undefined) patched.startDate = fields.startDate || null;
            if (fields.completionDate !== undefined) patched.completionDate = fields.completionDate || null;
            if (fields.assignedUserId !== undefined) patched.assignedUserId = fields.assignedUserId || null;
            if (fields.progress !== undefined) patched.progress = fields.progress;
            return patched;
          })
        };
      });
    }

    try {
      var response = await supabase
        .from('project_weekly_subtasks')
        .update(dbFields)
        .eq('id', subtaskId)
        .select()
        .single();

      if (response.error) {
        debugError('updateSubtask error:', response.error);
        return { success: false, error: response.error.message };
      }

      var updated = normalizeSubtask(response.data);
      debugLog('updateSubtask success:', updated.id);

      // Authoritative update from DB response
      var pid = updated.parentTaskId;
      setSubtasksByParent(function (prev) {
        var list = prev[pid] || [];
        return {
          ...prev,
          [pid]: list.map(function (s) { return s.id === subtaskId ? updated : s; })
        };
      });
      return { success: true, data: updated };
    } catch (err) {
      debugError('updateSubtask exception:', err);
      return { success: false, error: err.message };
    }
  };

  // deleteSubtask(subtaskId) — 1 arg, resolves parent internally
  var deleteSubtask = async function (subtaskId) {
    if (!subtaskId) return { success: false, error: 'Missing subtask ID' };

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    debugLog('deleteSubtask', subtaskId);

    // Resolve parent before deletion
    var resolvedParent = findParentForSubtask(subtasksRef.current, subtaskId);

    try {
      var response = await supabase
        .from('project_weekly_subtasks')
        .delete()
        .eq('id', subtaskId);

      if (response.error) {
        debugError('deleteSubtask error:', response.error);
        if (response.error.code === '42501') {
          return { success: false, error: 'Permission denied. Only supervisors and admins can delete subtasks.' };
        }
        return { success: false, error: response.error.message };
      }

      debugLog('deleteSubtask success:', subtaskId);

      if (resolvedParent) {
        setSubtasksByParent(function (prev) {
          var list = prev[resolvedParent] || [];
          return {
            ...prev,
            [resolvedParent]: list.filter(function (s) { return s.id !== subtaskId; })
          };
        });
      }
      return { success: true };
    } catch (err) {
      debugError('deleteSubtask exception:', err);
      return { success: false, error: err.message };
    }
  };

  // ── Realtime subscriptions (debounced, filtered by project_id) ─────────
  var subscriptionsRef = useRef([]);
  var debouncedRefetch = useDebouncedCallback(function () {
    debugLog('Realtime change detected — refetching');
    isFetchingRef.current = false;
    lastKeyRef.current = null;
    fetchTasks(true);
  }, 300);

  useEffect(function () {
    var numericProjectId = toProjectId(projectId);
    if (!enableRealtime || !numericProjectId) return;

    debugLog('Subscribing to realtime for project', numericProjectId);

    var tasksSub = supabase
      .channel('weekly-tasks-rt-' + numericProjectId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_weekly_tasks',
          filter: 'project_id=eq.' + numericProjectId
        },
        function () { debouncedRefetch(); }
      )
      .subscribe();

    var subtasksSub = supabase
      .channel('weekly-subtasks-rt-' + numericProjectId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_weekly_subtasks'
        },
        function () { debouncedRefetch(); }
      )
      .subscribe();

    subscriptionsRef.current = [tasksSub, subtasksSub];

    return function () {
      debugLog('Unsubscribing realtime for project', numericProjectId);
      subscriptionsRef.current.forEach(function (sub) {
        supabase.removeChannel(sub);
      });
      subscriptionsRef.current = [];
    };
  }, [projectId, enableRealtime, debouncedRefetch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Refetch when tab becomes visible ──────────────────────────────────
  // Prevents stale tabs from overwriting newer DB state. When a tab has been
  // dormant (laptop sleep, backgrounded overnight, Chrome restore on next
  // morning launch) the realtime WebSocket may have silently disconnected,
  // leaving the tab with stale state. Forcing a refetch on visibility ensures
  // the user sees current DB state before making any edits.
  useEffect(function () {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      var numericProjectId = toProjectId(projectId);
      if (!numericProjectId || !weekId) return;
      debugLog('Tab became visible — refetching to clear stale state');
      if (refetchStableRef.current) refetchStableRef.current();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return function () {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [projectId, weekId]);

  // ── Reorder tasks locally (drag-and-drop shuffle) ──────────────────────
  // Moves the task with dragId to the position of dropId in the local array.
  // This is a client-side only reorder (no DB persistence).
  var reorderTasks = useCallback(function (dragId, dropId) {
    if (dragId === dropId) return;
    setTasks(function (prev) {
      var dragIdx = prev.findIndex(function (t) { return t.id === dragId; });
      var dropIdx = prev.findIndex(function (t) { return t.id === dropId; });
      if (dragIdx === -1 || dropIdx === -1) return prev;
      var next = prev.slice();
      var removed = next.splice(dragIdx, 1)[0];
      next.splice(dropIdx, 0, removed);
      return next;
    });
  }, []);

  return {
    tasks: tasks,
    subtasksByParent: subtasksByParent,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
    addTask: addTask,
    updateTask: updateTask,
    deleteTask: deleteTask,
    reorderTasks: reorderTasks,
    addSubtask: addSubtask,
    updateSubtask: updateSubtask,
    deleteSubtask: deleteSubtask
  };
}
